import React, { useState } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/services/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { GlobalHeader } from '@/components/GlobalHeader'
import toast from 'react-hot-toast'

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setUserFromPasswordReset = useAuthStore((s) => s.setUserFromPasswordReset)

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background-light to-secondary/5">
        <GlobalHeader showNavigation={false} />
        <div className="flex items-center justify-center p-4 pt-24">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-text-primary mb-4">
              {t('auth.resetPasswordInvalidLink', 'Invalid or missing reset link')}
            </h2>
            <p className="text-text-secondary mb-6">
              {t('auth.resetPasswordRequestNew', 'Please request a new password reset link from the login page.')}
            </p>
            <Link to="/login" className="text-primary font-medium hover:underline">
              {t('auth.backToLogin', 'Back to login')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (newPassword.length < 12) {
      setError(t('auth.passwordMinLength', 'Password must be at least 12 characters'))
      return
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch', 'Passwords do not match'))
      return
    }

    setIsLoading(true)
    try {
      const response = await authApi.confirmPasswordReset({
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      })
      const user = response.data?.data?.user
      if (user) {
        setUserFromPasswordReset(user)
        toast.success(t('auth.passwordResetSuccess', 'Password updated. You are now logged in.'))
        navigate('/', { replace: true })
      } else {
        setError(t('auth.resetPasswordError', 'Something went wrong. Please try again.'))
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || t('auth.resetPasswordError', 'Something went wrong. Please try again.')
      setError(msg)
      toast.error(msg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background-light to-secondary/5">
      <GlobalHeader showNavigation={false} />
      <div className="flex items-center justify-center p-4 pt-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <img
              src="/assets/djelo-sem-fundo.png"
              alt="Djelo"
              className="w-40 h-auto object-contain mx-auto mb-4"
            />
            <p className="text-text-secondary">
              {t('common.walletDescription', 'Sua carteira digital em São Tomé e Príncipe')}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-text-primary mb-2 text-center">
              {t('auth.setNewPassword', 'Set new password')}
            </h2>
            <p className="text-text-secondary text-sm text-center mb-6">
              {t('auth.setNewPasswordDescription', 'Enter your new password below. Use at least 12 characters.')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder={t('auth.newPassword', 'New password')}
                value={newPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                error={error && newPassword.length > 0 && newPassword.length < 12 ? error : undefined}
                required
                className="text-lg"
              />
              <Input
                type="password"
                placeholder={t('auth.confirmPassword', 'Confirm password')}
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                error={newPassword !== confirmPassword && confirmPassword ? t('auth.passwordsDoNotMatch', 'Passwords do not match') : undefined}
                required
                className="text-lg"
              />
              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
              <Button
                type="submit"
                className="w-full mobile-button"
                disabled={isLoading || newPassword.length < 12 || newPassword !== confirmPassword}
                loading={isLoading}
              >
                {isLoading ? t('common.loading') : t('auth.updatePassword', 'Update password')}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-text-secondary">
              <Link to="/login" className="text-primary font-medium hover:underline">
                {t('auth.backToLogin', 'Back to login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
